import type { GuidePricing } from '@/types';

const CURRENCY_SYMBOLS: Record<string, string> = {
  INR: '₹',
  USD: '$',
  EUR: '€',
  GBP: '£',
};

export function formatGuidePrice(
  amount: number | null | undefined,
  currency = 'INR'
): string | null {
  if (amount == null) return null;

  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    const symbol = CURRENCY_SYMBOLS[currency] ?? currency;
    return `${symbol}${amount.toLocaleString()}`;
  }
}

export function getLowestGuidePrice(pricing?: GuidePricing | null): {
  amount: number;
  label: string;
} | null {
  if (!pricing) return null;

  const options: { amount: number; label: string }[] = [];
  if (pricing.hourlyPrice != null && pricing.hourlyPrice > 0) {
    options.push({ amount: pricing.hourlyPrice, label: 'hour' });
  }
  if (pricing.fullDayPrice != null && pricing.fullDayPrice > 0) {
    options.push({ amount: pricing.fullDayPrice, label: 'full day' });
  }

  if (options.length === 0) return null;

  return options.reduce((lowest, current) =>
    current.amount < lowest.amount ? current : lowest
  );
}

export function getPricingSummary(pricing?: GuidePricing | null): string | null {
  const lowest = getLowestGuidePrice(pricing);
  if (!lowest || !pricing) return null;
  const formatted = formatGuidePrice(lowest.amount, pricing.currency);
  return formatted ? `From ${formatted} / ${lowest.label}` : null;
}

export function isPricingConfigured(pricing?: GuidePricing | null): boolean {
  if (!pricing) return false;
  return (
    pricing.hourlyPrice != null &&
    pricing.hourlyPrice > 0 &&
    pricing.fullDayPrice != null &&
    pricing.fullDayPrice > 0
  );
}

/** True when pricing exists so the guide can appear in traveler search (matches list filter). */
export function isGuideMarketplaceLive(pricing?: GuidePricing | null): boolean {
  return getLowestGuidePrice(pricing) != null;
}
